import { useEffect, useMemo } from "react";
import type { FormEvent, KeyboardEvent } from "react";
import { useFieldArray, useForm } from "react-hook-form";
import FormModal from "@/components/FormModal/FormModal";
import Button from "@/components/Button/Button";
import ErrorText from "@/components/ErrorText/ErrorText";
import PlusIcon from "@/components/SVGComponents/PlusIcon";
import CrossIcon from "@/components/SVGComponents/CrossIcon";
import { useAppDispatch, useAppSelector } from "@/store/hooks";
import { getCategories } from "@/store/slices/CategorySlice";
import { getDependentQuestions, postQuestion, updateQuestion } from "@/store/slices/QuestionSlice";
import { assignToOptions, questionTypeOptions } from "@/types/question";
import type { PostQuestionRequest, QuestionField, UserType } from "@/types/question";
import { combineClasses, requiredMessage } from "@/lib/utils";
import { numberRegex } from "@/lib/regex";
import formRenderer from "@/lib/formRenderer";
import type { FormConfig } from "@/lib/formRenderer";
import toast from "@/lib/toast";
import {
  getDependentQuestions as getDependentQuestionsOf,
  getParentQuestionId,
  isYesNoQuestion,
  YES_NO_FIELD_TYPE,
} from "../lib/questionDependencies";
import styles from "./AddQuestionModal.module.scss";

interface SelectOption {
  label: string;
  value: string;
}

interface AddQuestionFormValues {
  questionType: SelectOption | null;
  description?: string;
  assignTo: SelectOption | null;
  category: SelectOption | null;
  questionTitle: string;
  displayOrder: null | number;
  options: { value: string }[];
  dependency: boolean;
  dependentQuestionId: SelectOption | null;
}

const EMPTY_VALUES: AddQuestionFormValues = {
  questionType: null,
  assignTo: null,
  category: null,
  questionTitle: "",
  description: "",
  displayOrder: null,
  options: [],
  dependency: false,
  dependentQuestionId: null,
};

export interface AddQuestionModalProps {
  isOpen: boolean;
  onClose: () => void;
  questionToEdit: QuestionField | null;
  userType: UserType;
  onSaved: () => void;
}

const AddQuestionModal = ({ isOpen, onClose, questionToEdit, userType, onSaved }: AddQuestionModalProps) => {
  const dispatch = useAppDispatch();
  const { isPosting, isUpdating, questions, dependentQuestions } = useAppSelector((state) => state.question);
  const { categories } = useAppSelector((state) => state.category);

  const isEdit = Boolean(questionToEdit);
  const categoryOptions: SelectOption[] = categories.map((cat) => ({ label: cat.name, value: String(cat.id) }));
  const isSubmitting = isPosting || isUpdating;

  const {
    register,
    handleSubmit,
    watch,
    control,
    setValue,
    setError,
    reset,
    formState,
  } = useForm<AddQuestionFormValues>({
    mode: "onChange",
    defaultValues: EMPTY_VALUES,
  });
  const { errors } = formState;

  const { fields, append, remove } = useFieldArray({ control, name: "options" });
  const selectedQuestionType = watch("questionType");
  const hasDependency = watch("dependency");

  useEffect(() => {
    if (!hasDependency) {
      setValue("dependentQuestionId", null);
    }
  }, [hasDependency, setValue]);

  useEffect(() => {
    if (!isOpen) return;
    dispatch(getCategories({ perPage: 100 }));
    dispatch(getDependentQuestions({ search: "", pageNo: 1, perPage: 100, userType, isDependentEligible: true }));
  }, [isOpen, userType, dispatch]);

  useEffect(() => {
    if (!isOpen) return;

    if (!questionToEdit) {
      reset({ ...EMPTY_VALUES, assignTo: assignToOptions.find((opt) => opt.value === userType) ?? null });
      return;
    }

    reset({
      questionType: questionTypeOptions.find((opt) => opt.value === questionToEdit.fieldType) ?? null,
      assignTo: assignToOptions.find((opt) => opt.value === questionToEdit.userType) ?? null,
      category: categoryOptions.find((opt) => opt.label === questionToEdit.categoryName) ?? null,
      questionTitle: questionToEdit.label || "",
      description: questionToEdit.description ?? "",
      displayOrder: questionToEdit.displayOrder !== null ? questionToEdit.displayOrder : null,
      options: questionToEdit.questionOptionsResponse?.map((opt) => ({ value: opt.optionLabel })) || [],
      dependency: questionToEdit.validationRules?.shouldShowIf === "Yes",
      dependentQuestionId:
        [...dependentQuestions, ...questions]
          .filter((q) => q.id === questionToEdit.validationRules?.dependentQuestionId)
          .map((q) => ({ label: q.label, value: String(q.id) }))[0] ?? null,
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, questionToEdit]);

  useEffect(() => {
    // if (isEdit) return;

    if (selectedQuestionType?.value === "checkbox") {
      if (fields.length === 0) {
        append({ value: "" });
        append({ value: "" });
      }
    } else if (selectedQuestionType?.value === "radio") {
      setValue("options", [{ value: "Yes" }, { value: "No" }]);
    } else {
      setValue("options", []);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedQuestionType?.value]);

  // Only Yes/No questions can be depended on (the condition is "answered
  // Yes"). Also excluded: the question itself, and any question that
  // already depends on it — picking one of those would create a loop.
  const dependentQuestionOptions: SelectOption[] = dependentQuestions
    .filter(
      (question) =>
        isYesNoQuestion(question) &&
        question.id !== questionToEdit?.id &&
        (!questionToEdit || getParentQuestionId(question) !== questionToEdit.id),
    )
    .map((question) => ({ label: question.label, value: String(question.id) }));

  // A dependent question must be ordered after the question it depends on,
  // and (when editing that question) before every question depending on it.
  // Returns the error message for `displayOrder`, or null when it's valid.
  const getDisplayOrderError = (displayOrder: number, parentId: number | null): string | null => {
    const knownQuestions = [...dependentQuestions, ...questions];
    const parent = parentId !== null ? knownQuestions.find((question) => question.id === parentId) : undefined;
    if (parent && displayOrder <= parent.displayOrder) {
      return `Must be after "${parent.label}" (display order ${parent.displayOrder})`;
    }

    if (questionToEdit) {
      const earliestDependent = getDependentQuestionsOf(questionToEdit.id, questions)
        .sort((a, b) => a.displayOrder - b.displayOrder)[0];
      if (earliestDependent && displayOrder >= earliestDependent.displayOrder) {
        return `Must be before "${earliestDependent.label}" (display order ${earliestDependent.displayOrder}), which depends on this question`;
      }
    }
    return null;
  };

  // Fixed fields — config-driven via `formRenderer` (same convention as
  // AddMemberModal/AddVenueModal), since these have no conditional
  // rendering of their own. `category`'s options are the only part that
  // change across renders (fetched from Redux), hence the `useMemo`.
  const gridFields: FormConfig = useMemo(
    () => ({
      questionType: {
        controlType: "select",
        label: "Question Type",
        placeholder: "Select question type",
        labelVariant: "caption",
        isControlled: true,
        isRequired: true,
        isDisabled: isSubmitting,
        validations: { required: requiredMessage("Question type") },
        props: { options: questionTypeOptions, isSearchable: false, className: combineClasses(styles.fieldSelect) },
      },
      assignTo: {
        controlType: "select",
        label: "Assign To",
        labelVariant: "caption",
        placeholder: "Select assign to",
        isControlled: true,
        isRequired: true,
        isDisabled: isSubmitting,
        validations: { required: requiredMessage("Assign to") },
        props: { options: assignToOptions, isSearchable: false, className: combineClasses(styles.fieldSelect) },
      },
      category: {
        controlType: "select",
        label: "Category",
        labelVariant: "caption",
        placeholder: "Select category",
        isControlled: true,
        isDisabled: isSubmitting,
        props: { options: categoryOptions, isSearchable: false, className: combineClasses(styles.fieldSelect) },
      },
      displayOrder: {
        controlType: "input",
        label: "Display Order (number)",
        labelVariant: "caption",
        isRequired: true,
        isDisabled: isSubmitting,
        validations: {
          required: requiredMessage("Display order"),
          pattern: { value: numberRegex, message: "Only numbers are allowed" },
          min: { value: 1, message: "Display order must be at least 1" },
          max: { value: 999, message: "Display order must be at most 999" },
          maxLength: { value: 3, message: "Display order must be at most 3 digits" },
        },

        props: {
          type: "text",
          inputMode: "numeric",
          maxLength: 3,
          className: combineClasses(styles.fieldInput),
          onKeyDown: (e: KeyboardEvent<HTMLInputElement>) => {
            if (
              !/^\d$/.test(e.key) &&
              !["Backspace", "Delete", "Tab", "ArrowLeft", "ArrowRight", "Enter"].includes(e.key) &&
              !e.ctrlKey &&
              !e.metaKey
            ) {
              e.preventDefault();
            }
          },
          onInput: (e: FormEvent<HTMLInputElement>) => {
            if (e.currentTarget.value.length > 3) {
              e.currentTarget.value = e.currentTarget.value.slice(0, 3);
            }
          },
        },
      },
    }),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [categoryOptions.length, isSubmitting],
  );

  const questionTitleField: FormConfig = useMemo(
    () => ({
      questionTitle: {
        controlType: "textarea",
        label: "Question",
        labelVariant: "caption",
        placeholder: "Write the question",
        isRequired: true,
        isDisabled: isSubmitting,
        validations: {
          required: requiredMessage("Question"),
          maxLength: { value: 500, message: "Question must be under 500 characters" },
        },
      },
      description: {
        controlType: "textarea",
        label: "Description",
        labelVariant: "caption",
        placeholder: "Write the description",
        isRequired: false,
        isDisabled: isSubmitting,
        validations: { maxLength: { value: 1000, message: "Description must be under 1000 characters" } },
      },
    }),
    [isSubmitting],
  );

  const dependencyFields: FormConfig = useMemo(
    () => ({
      dependency: { controlType: "checkbox", label: "Depends on another question", isDisabled: isSubmitting },
      dependentQuestionId: {
        controlType: "select",
        label: "Dependent Question",
        labelVariant: "caption",
        isControlled: true,
        isRequired: hasDependency,
        hide: !hasDependency,
        isDisabled: isSubmitting,
        ...(hasDependency ? { validations: { required: requiredMessage("Dependent question") } } : {}),
        props: { options: dependentQuestionOptions, isSearchable: false, className: combineClasses(styles.fieldSelect) },
      },
    }),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [hasDependency, dependentQuestionOptions.length, isSubmitting],
  );

  const onSubmit = (data: AddQuestionFormValues) => {
    if (!data.questionType || !data.assignTo) return;

    const parentId =
      data.dependency && data.dependentQuestionId ? Number.parseInt(data.dependentQuestionId.value, 10) : null;
    const displayOrderError = getDisplayOrderError(Number(data.displayOrder), parentId);
    if (displayOrderError) {
      setError("displayOrder", { type: "dependencyOrder", message: displayOrderError }, { shouldFocus: true });
      return;
    }

    // Other questions depend on this one being answered "Yes" — it can't
    // stop being a Yes/No question while they do.
    if (questionToEdit && data.questionType.value !== YES_NO_FIELD_TYPE) {
      const dependentCount = getDependentQuestionsOf(questionToEdit.id, questions).length;
      if (dependentCount > 0) {
        setError("questionType", {
          type: "dependencyType",
          message: `${dependentCount} question${dependentCount === 1 ? " depends" : "s depend"} on this one, so it must stay Yes/No`,
        });
        return;
      }
    }

    const requestData: PostQuestionRequest = {
      ...(data.category ? { questionCategoryId: Number.parseInt(data.category.value, 10) } : {}),
      userType: data.assignTo.value as UserType,
      label: data.questionTitle,
      ...(data.description !== undefined ? { description: data.description } : {}),
      fieldType: data.questionType.value,
      displayOrder: data.displayOrder ? Number(data.displayOrder) : 0,
      validationRules: {
        shouldShowIf: data.dependency ? "Yes" : "No",
        dependentQuestionId: data.dependentQuestionId ? Number.parseInt(data.dependentQuestionId.value, 10) : null,
      },
      options:
        data.questionType.value === "checkbox" || data.questionType.value === "radio"
          ? data.options.map((opt, index) => ({
              optionLabel: opt.value,
              optionValue: opt.value,
              displayOrder: index + 1,
            }))
          : [],
    };

    const submitPromise = isEdit
      ? dispatch(updateQuestion({ id: String(questionToEdit?.id), data: requestData }))
      : dispatch(postQuestion(requestData));

    submitPromise
      .unwrap()
      .then(() => {
        toast.success(isEdit ? "Question updated successfully" : "Question added successfully");
        reset(EMPTY_VALUES);
        onSaved();
        onClose();
      })
      .catch(() => {
        // privateRequest's response interceptor already shows an error toast
        // (see services/axios.ts) — nothing more to do here.
      });
  };

  const handleClose = () => {
    reset(EMPTY_VALUES);
    onClose();
  };

  const submitLabel = isSubmitting ? (isEdit ? "Updating..." : "Adding...") : isEdit ? "Update" : "Add";

  return (
    <FormModal
      isOpen={isOpen}
      onClose={handleClose}
      title={isEdit ? "Edit Question" : "Add Question"}
      onSubmit={handleSubmit(onSubmit)}
      submitLabel={submitLabel}
      submitDisabled={isSubmitting}
      className={combineClasses(styles.questionModal)}
    >
      <div className={styles.fieldGrid}>{formRenderer<AddQuestionFormValues>(gridFields, formState, register, control)}</div>

      {formRenderer<AddQuestionFormValues>(questionTitleField, formState, register, control)}

      {(selectedQuestionType?.value === "checkbox" || selectedQuestionType?.value === "radio") && (
        <div className={styles.optionsList}>
          {fields.map((field, index) => {
            const optionError = errors.options?.[index]?.value?.message;
            const isLocked = selectedQuestionType.value === "radio";

            return (
              <div key={field.id} className={styles.optionRow}>
                <label className={styles.optionLabel}>Option {index + 1}</label>
                <div className={styles.optionInputWrap}>
                  <input
                    {...register(`options.${index}.value`, {
                      required: requiredMessage(`Option ${index + 1}`),
                      maxLength: { value: 150, message: `Option ${index + 1} must be under 150 characters` },
                    })}
                    type="text"
                    disabled={isLocked || isSubmitting}
                    placeholder={`Enter Option ${index + 1}`}
                    className={styles.optionInput}
                  />
                  {!isLocked && fields.length > 2 && (
                    <button
                      type="button"
                      aria-label={`Remove option ${index + 1}`}
                      onClick={() => remove(index)}
                      disabled={isSubmitting}
                      className={styles.removeOptionBtn}
                    >
                      <CrossIcon withBg={false} stroke="currentColor" strokeWidth={1.5} width={14} height={14} />
                    </button>
                  )}
                </div>
                {optionError && <ErrorText>{optionError}</ErrorText>}
              </div>
            );
          })}

          {selectedQuestionType.value === "checkbox" && (
            <Button
              type="button"
              outlined
              onClick={() => append({ value: "" })}
              disabled={isSubmitting}
              className={combineClasses(styles.addOptionBtn)}
            >
              <PlusIcon />
              Add Option
            </Button>
          )}
        </div>
      )}

      {formRenderer<AddQuestionFormValues>(dependencyFields, formState, register, control)}
    </FormModal>
  );
};

export default AddQuestionModal;
